import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { Copy, Trash2, Plus, Webhook } from '@tamagui/lucide-icons'
import { useState } from 'react'
import { useAppStore, useServerStore } from '@/store'
import { webhooks, users } from '@/mocks'

const Container = styled(View, { flex: 1, flexDirection: 'column' })

const Header = styled(View, {
  paddingHorizontal: 32, paddingVertical: 20,
  borderBottomWidth: 1, borderBottomColor: '$borderDefault',
  flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
})

const Title = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground',
})

const Body = styled(View, { padding: 32, gap: 16, maxWidth: 700 })

const CreateButton = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
  height: 36,
  paddingHorizontal: 16,
  borderRadius: '$borderRadiusMd',
  backgroundColor: '$accent',
  cursor: 'pointer',
  alignSelf: 'flex-start',
  hoverStyle: { opacity: 0.9 },
})

const CreateForm = styled(View, {
  padding: 16,
  backgroundColor: '$sidebar',
  borderRadius: '$borderRadiusLg',
  borderWidth: 1,
  borderColor: '$borderDefault',
  gap: 12,
})

const WebhookCard = styled(View, {
  padding: 16,
  backgroundColor: '$sidebar',
  borderRadius: '$borderRadiusLg',
  borderWidth: 1,
  borderColor: '$borderDefault',
  gap: 10,
})

const ActionButton = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  height: 28,
  paddingHorizontal: 10,
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  borderColor: '$borderDefault',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const DeleteBtn = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  height: 28,
  paddingHorizontal: 10,
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  borderColor: '$error',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: 'rgba(207, 62, 79, 0.1)' },
})

const SubmitButton = styled(View, {
  height: 32,
  paddingHorizontal: 16,
  borderRadius: '$borderRadiusMd',
  backgroundColor: '$accent',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  alignSelf: 'flex-start',
  hoverStyle: { opacity: 0.9 },
})

const Label = styled(Text, {
  fontFamily: '$fontBody', fontSize: 12, fontWeight: '600',
  color: '$color8', textTransform: 'uppercase', letterSpacing: 0.5,
})

export function WebhookSettings() {
  const { activeServerId } = useAppStore()
  const server = useServerStore((s) => activeServerId ? s.servers[activeServerId] : undefined)
  const theme = useTheme()
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newName, setNewName] = useState('')
  const [newChannelId, setNewChannelId] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const serverWebhooks = activeServerId ? webhooks[activeServerId] || [] : []
  const channels = server?.channels.filter(c => c.type === 'text' || c.type === 'announcement') || []

  const handleCopyUrl = (webhookId: string, url: string) => {
    navigator.clipboard.writeText(url)
    setCopiedId(webhookId)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const handleCreate = () => {
    // Mock create action
    setShowCreateForm(false)
    setNewName('')
    setNewChannelId('')
  }

  return (
    <Container>
      <Header>
        <Title>Webhooks</Title>
      </Header>
      <ScrollView flex={1}>
        <Body>
          {!showCreateForm ? (
            <CreateButton onPress={() => setShowCreateForm(true)}>
              <Plus size={14} color="$surface" />
              <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">Create Webhook</Text>
            </CreateButton>
          ) : (
            <CreateForm>
              <Text fontFamily="$fontBody" fontWeight="600" fontSize={14} color="$surfaceForeground">
                New Webhook
              </Text>
              <View gap={4}>
                <Label>Name</Label>
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. GitHub Bot"
                  style={{
                    background: 'transparent',
                    border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                    borderRadius: 4, padding: '6px 10px',
                    color: theme.surfaceForeground?.val || '#E2E8F0',
                    fontFamily: 'inherit', fontSize: 13,
                    outline: 'none', width: '100%', boxSizing: 'border-box',
                  }}
                />
              </View>
              <View gap={4}>
                <Label>Channel</Label>
                <select
                  value={newChannelId}
                  onChange={(e) => setNewChannelId(e.target.value)}
                  style={{
                    background: theme.sidebar?.val || '#151921',
                    border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                    borderRadius: 4,
                    padding: '6px 10px',
                    color: theme.surfaceForeground?.val || '#E2E8F0',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    outline: 'none',
                    width: '100%',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">Select a channel...</option>
                  {channels.map((ch) => (
                    <option key={ch.id} value={ch.id}>#{ch.name}</option>
                  ))}
                </select>
              </View>
              <View flexDirection="row" gap={8}>
                <SubmitButton onPress={handleCreate}>
                  <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">Create</Text>
                </SubmitButton>
                <View
                  height={32} paddingHorizontal={16} borderRadius="$borderRadiusMd"
                  alignItems="center" justifyContent="center" cursor="pointer"
                  borderWidth={1} borderColor="$borderDefault"
                  hoverStyle={{ backgroundColor: '$sidebarHover' }}
                  onPress={() => { setShowCreateForm(false); setNewName(''); setNewChannelId('') }}
                >
                  <Text fontFamily="$fontBody" fontSize={13} color="$sidebarMuted">Cancel</Text>
                </View>
              </View>
            </CreateForm>
          )}

          {serverWebhooks.map((wh) => {
            const creator = users[wh.createdBy]
            const channel = channels.find(c => c.id === wh.channelId)
            return (
              <WebhookCard key={wh.id}>
                <View flexDirection="row" alignItems="center" gap={10}>
                  <View
                    width={36} height={36} borderRadius="$borderRadiusMd"
                    backgroundColor="$sidebarActive" alignItems="center" justifyContent="center"
                  >
                    <Webhook size={18} color="$accent" />
                  </View>
                  <View flex={1} gap={2}>
                    <Text fontFamily="$fontBody" fontWeight="600" fontSize={14} color="$surfaceForeground">
                      {wh.name}
                    </Text>
                    <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">
                      #{channel?.name || 'unknown'} — created by {creator?.displayName || 'Unknown'}
                    </Text>
                  </View>
                </View>

                <View
                  paddingHorizontal={10} paddingVertical={6}
                  backgroundColor="$surface" borderRadius="$borderRadiusSm"
                  borderWidth={1} borderColor="$borderDefault"
                >
                  <Text fontFamily="$fontMono" fontSize={11} color="$color8" numberOfLines={1}>
                    {wh.url}
                  </Text>
                </View>

                <View flexDirection="row" gap={8}>
                  <ActionButton onPress={() => handleCopyUrl(wh.id, wh.url)}>
                    <Copy size={12} color="$sidebarMuted" />
                    <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">
                      {copiedId === wh.id ? 'Copied!' : 'Copy URL'}
                    </Text>
                  </ActionButton>
                  <DeleteBtn>
                    <Trash2 size={12} color="$error" />
                    <Text fontFamily="$fontBody" fontSize={12} color="$error">Delete</Text>
                  </DeleteBtn>
                </View>
              </WebhookCard>
            )
          })}

          {serverWebhooks.length === 0 && !showCreateForm && (
            <View padding={32} alignItems="center" gap={8}>
              <Webhook size={32} color="$color8" />
              <Text fontFamily="$fontBody" fontSize={14} color="$sidebarMuted">
                No webhooks configured yet.
              </Text>
            </View>
          )}
        </Body>
      </ScrollView>
    </Container>
  )
}
